'use client';

import Link from 'next/link';
import { useTranslation } from 'react-i18next';

import { organizationPath } from '@kit/shared/handles';
import { Avatar, AvatarFallback, AvatarImage } from '@kit/ui/avatar';

import { DancerSection } from './dancer-section';
import { isAwardExperience } from './experience-partition';
import type { DancerExperience as Experience } from './types';

interface Props {
  experiences: Experience[];
}

/**
 * Experience timeline — logo-left editorial composition. Each row:
 *
 *   [org logo] | mono date | org name (linked when public, else manual text)
 *                          | role + descriptor (italic)
 *
 * Hairline rules between rows, reverse-chronological, current entries first.
 * Renders ENGAGEMENTS only — award/competition/placement credits are
 * partitioned into the dedicated `DancerAwards` section (see
 * `experience-partition.ts`). The section self-hides when its bucket is empty.
 */
export function DancerExperience({ experiences }: Props) {
  const { t } = useTranslation();

  // Engagements = everything that is NOT an award/competition/placement row.
  // Drop content-less rows (~12% of migrated experiences are empty shells —
  // a date and nothing else); a bare date carries no signal.
  const sorted = sortExperience(
    experiences.filter((e) => !isAwardExperience(e)),
  ).filter(hasExperienceContent);
  if (sorted.length === 0) return null;

  const currentLabel = t('dancer:publicView.experience.current');

  return (
    <DancerSection
      eyebrow={t('dancer:publicView.experience.eyebrow')}
      title={t('dancer:publicView.experience.title')}
      count={sorted.length}
    >
      <ol className="divide-foreground/10 divide-y">
        {sorted.map((exp) => {
          const dateLabel = formatRange(exp, currentLabel);
          const org = exp.organization;
          const orgName = org?.name ?? exp.organization_manual ?? '';
          // Role: the explicit title, else the dancer's rank (resolved from
          // the ranks join in the loader), else the type label. Reading the
          // rank before the type label keeps the headline off the generic
          // "Other" fallback for ranked credits (BAL-720).
          const role =
            exp.title ?? exp.rank_title ?? capitalise(exp.type ?? '');
          // Always non-empty: hasExperienceContent guarantees at least one of
          // these is set, so the headline never falls back to a placeholder.
          const headline = orgName || role || exp.rank_title || '';
          const descriptor = composeDescriptor(exp);
          const initials = headline.trim().slice(0, 2).toUpperCase() || '—';

          return (
            <li
              key={exp.id}
              className="grid grid-cols-12 items-start gap-x-4 gap-y-2 py-6 sm:py-7"
            >
              {/* Logo column — Avatar with initials fallback (always present
                  so the timeline stays visually aligned even for manual-only
                  or logo-less orgs). */}
              <div className="col-span-2 sm:col-span-1">
                <Avatar className="h-10 w-10">
                  {org?.logoUrl && (
                    <AvatarImage src={org.logoUrl} alt={`${org.name} logo`} />
                  )}
                  <AvatarFallback className="text-xs font-medium">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </div>

              <div className="text-muted-foreground/80 col-span-10 font-mono text-[11px] tracking-[0.18em] uppercase tabular-nums sm:col-span-3 sm:pt-2">
                {dateLabel}
              </div>

              <div className="col-span-12 sm:col-span-8">
                <div className="font-heading text-foreground text-xl leading-tight tracking-[-0.01em] sm:text-2xl">
                  {org ? (
                    <Link
                      href={organizationPath(org.handle)}
                      className="decoration-foreground/25 hover:text-foreground/70 hover:decoration-foreground/60 underline decoration-1 underline-offset-4 transition-colors"
                    >
                      {org.name}
                    </Link>
                  ) : (
                    headline
                  )}
                </div>
                {descriptor && (
                  <div className="text-muted-foreground mt-1 text-base leading-relaxed italic">
                    {descriptor}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </DancerSection>
  );
}

/**
 * A row is worth rendering only if it carries at least one piece of content
 * beyond its date. Empty shells (date-only migrated rows) are dropped so the
 * timeline never shows "Untitled engagement".
 */
function hasExperienceContent(exp: Experience): boolean {
  return Boolean(
    exp.organization?.name?.trim() ||
    exp.organization_manual?.trim() ||
    exp.title?.trim() ||
    exp.type?.trim() ||
    exp.rank_title?.trim(),
  );
}

function sortExperience(items: Experience[]): Experience[] {
  return [...items].sort((a, b) => {
    if (a.current && !b.current) return -1;
    if (!a.current && b.current) return 1;
    const ay = a.start_date ?? '';
    const by = b.start_date ?? '';
    return by.localeCompare(ay);
  });
}

function formatRange(exp: Experience, currentLabel: string): string {
  const start = exp.start_date ? exp.start_date.slice(0, 4) : '';
  if (exp.current) {
    return start ? `${start} –` : currentLabel;
  }
  if (start && exp.end_date) {
    const end = exp.end_date.slice(0, 4);
    if (end === start) return start;
    // Compact two-digit end year for ranges (e.g. "2022 – 23").
    return `${start} – ${end.slice(2)}`;
  }
  return start || '—';
}

function composeDescriptor(exp: Experience): string {
  // The descriptor is the italic "context" line beneath the org name.
  const parts: string[] = [];
  if (exp.title) parts.push(exp.title);
  if (exp.rank_title) parts.push(exp.rank_title);
  if (exp.type && parts.length === 0) parts.push(capitalise(exp.type));
  return parts.join(' · ');
}

function capitalise(s: string): string {
  if (!s) return s;
  return s[0]!.toUpperCase() + s.slice(1);
}
